import { PropsWithChildren, useMemo } from 'react'
import styles from './SimulationRegistrationSideCardLayout.module.scss'
import ProducerLocation from 'components/producers/producer-location/ProducerLocation'
import { SimulationRegistrationSideCardLayoutProps } from 'components/simulation-registration-side-card-layout/types'
import { getCapacityState, getProducerLocation } from 'utils/producers'
import { useTranslation } from 'next-i18next/pages'
import classNames from 'classnames'
import { CapacityState } from 'types/producer'
import { useStoreSelector } from 'hooks/store'
import useSelectInputGroup from 'hooks/useSelectInputGroup'
import { InputGroupKeys } from 'store/customer/enums'
import { Img, LoadingSkeleton } from '@boltenergy-be/design-system'
import SideCardAction from 'components/simulation-registration-side-card-layout/components/side-card-action/SideCardAction'
import { useGetLanguage } from 'hooks/useGetLanguage'

const SimulationRegistrationSideCardLayout = ({
  producer,
  hideProducerImage,
  hideProducerLocation,
  className,
  headerClass,
  children,
  loading
}: PropsWithChildren<SimulationRegistrationSideCardLayoutProps>) => {
  // REDUX
  const { didSimulation } = useStoreSelector((store) => store.customer.flows.simulation)
  const simulation = useSelectInputGroup(InputGroupKeys.SIMULATION)

  // i18n
  const { t } = useTranslation('producer')
  const language = useGetLanguage()

  // Constants
  const capacityState = useMemo<CapacityState | undefined>(
    () => (producer ? getCapacityState(producer, simulation, didSimulation) : undefined),
    [didSimulation, simulation, producer]
  )

  return !loading && producer ? (
    <section className={styles.card}>
      {/* CARD HEADER */}
      {!hideProducerImage && (
        <figure className={classNames(styles['card-header'], headerClass)}>
          <Img fill className={styles['producer-image']} publicId={producer.images.profile?.publicId ?? ''} alt={producer.name[language]} />
          {!!capacityState?.almostSoldOut && <span className={styles.tag}>{t(($) => $.status.almostSoldOut)}</span>}
          {!hideProducerLocation && (
            <ProducerLocation {...producer} location={getProducerLocation(language, producer?.location)} className={styles.location} />
          )}
        </figure>
      )}

      {/* CARD CONTENT */}
      <div className={classNames(styles['card-content-container'], { [styles['fully-boxed']]: hideProducerImage }, className)}>
        {children}
      </div>
    </section>
  ) : loading ? (
    <div className={classNames(styles.card, styles.skeleton)}>
      <LoadingSkeleton className="gap-none">
        <LoadingSkeleton.Rectangle width="100%" height={250} radius="none" className={styles['img-skeleton']} />
        <div className={classNames(styles['card-content-container'], 'gap-300 p-400 mt-none')}>
          <LoadingSkeleton.Paragraph lines={3} />
        </div>
      </LoadingSkeleton>
    </div>
  ) : (
    // TODO: error state because no producers were found
    <></>
  )
}

SimulationRegistrationSideCardLayout.Action = SideCardAction

export default SimulationRegistrationSideCardLayout
